import { createRouter, createWebHistory } from 'vue-router'
import authGuard from './guards/authGuard'
import { useAuthStore } from '@/stores/Auth'

import Login from '../views/Auth/Login.vue'
import Register from '../views/Auth/Register.vue'
import VerifyEmail from '../views/Auth/VerifyEmail.vue'
import OauthConsent from '../views/Auth/OauthConsent.vue'
import AppList from '../views/App/List.vue'
import AppDetails from '../views/App/Details.vue'
import AppCheckout from '../views/App/Checkout.vue'
import ForgotPassword from '../views/Auth/ForgotPassword.vue'
import ResetPassword from '../views/Auth/ResetPassword.vue'
import UserDetails from '../views/User/Details.vue'
import AdminUsersList from '../views/Admin/Users/List.vue'
import AdminUsersDetails from '../views/Admin/Users/Details.vue'
import TermsOfService from '@/views/Legal/TermsOfService.vue'
import PrivacyPolicy from '@/views/Legal/PrivacyPolicy.vue'
import CookieNotice from '@/views/Legal/CookieNotice.vue'
import AuthApi from '@/helpers/api/AuthApi'
import NotFound from '../views/NotFound.vue'
import ExportList from '../views/Export/List.vue'
import FeedbackFeedback from '../views/Feedback/components/Feedback.vue'
import { usePostHog } from '../composables/usePostHog'

/* GENERATOR(IMPORT) */

const router = createRouter({
	history: createWebHistory(import.meta.env.BASE_URL),
	routes: [
		{
			path: '/verify-email',
			name: 'verify-email-notice',
			component: VerifyEmail,
			meta: {
				noNavigation: true,
			},
		},
		{
			path: '/email/verify/:id/:hash',
			name: 'email-verification',
			component: () => {},
			beforeEnter: async (to) => {
				const auth = useAuthStore()
				await auth.getUser()

				if (!auth.user) {
					return { name: 'login' }
				}

				await new AuthApi().verifyEmail({
					id: to.params.id as string,
					hash: to.params.hash as string,
					expires: to.query.expires as string,
					signature: to.query.signature as string,
				})

				await auth.getUser()

				return { name: 'root' }
			},
			meta: {
				unprotected: true,
				noNavigation: true,
			},
		},
		{
			path: '/forgot-password',
			name: 'forgot-password',
			component: ForgotPassword,
			meta: {
				unprotected: true,
				noNavigation: true,
			},
		},
		{
			path: '/reset-password',
			name: 'reset-password',
			component: ResetPassword,
			meta: {
				unprotected: true,
				noNavigation: true,
			},
		},
		{
			path: '/login',
			name: 'login',
			component: Login,
			meta: {
				avoidUser: true,
				noNavigation: true,
			},
		},
		{
			path: '/register',
			name: 'register',
			component: Register,
			meta: {
				avoidUser: true,
				noNavigation: true,
			},
		},
		{
			path: '/auth/oauth/consent',
			name: 'oauth-consent',
			component: OauthConsent,
			meta: {
				noNavigation: true,
				featureFlags: ['beta-features'],
			},
		},
		{
			path: '/terms-of-service',
			name: 'terms-of-service',
			component: TermsOfService,
			meta: {
				unprotected: true,
				noNavigation: true,
			},
		},
		{
			path: '/privacy-policy',
			name: 'privacy-policy',
			component: PrivacyPolicy,
			meta: {
				unprotected: true,
				noNavigation: true,
			},
		},
		{
			path: '/cookie-notice',
			name: 'cookie-notice',
			component: CookieNotice,
			meta: {
				unprotected: true,
				noNavigation: true,
			},
		},
		{
			path: '/logout',
			name: 'logout',
			component: () => {},
			beforeEnter: async () => {
				const auth = useAuthStore()
				await auth.logout()
				return { name: 'login' }
			},
			meta: {
				unprotected: true,
				noNavigation: true,
			},
		},
		{
			path: '/apps',
			children: [
				{
					path: '',
					name: 'apps-list',
					component: AppList,
				},
				{
					path: '/apps/create',
					name: 'apps-create',
					component: AppList,
				},
				{
					path: '/apps/:id/checkout',
					name: 'apps-checkout',
					component: AppCheckout,
				},
				{
					path: '/apps/:id',
					name: 'apps-details',
					component: AppDetails,
				},
			],
		},
		{
			path: '/exports',
			children: [
				{
					path: '',
					name: 'exports-list',
					component: ExportList,
				},
			],
		},
		{
			path: '/feedback',
			children: [
				{
					path: '',
					name: 'feedback',
					component: FeedbackFeedback,
				},
			],
		},
		{
			path: '/account',
			children: [
				{
					path: '',
					name: 'account-details',
					component: UserDetails,
				},
			],
		},
		{
			path: '/admin/users',
			children: [
				{
					path: '',
					name: 'admin-users-list',
					component: AdminUsersList,
					meta: {
						roles: ['admin'],
					},
				},
				{
					path: ':id',
					name: 'admin-users-details',
					component: AdminUsersDetails,
					meta: {
						roles: ['admin'],
					},
				},
			],
		},
		{
			path: '/404-not-found',
			name: 'not-found',
			component: NotFound,
			meta: {
				unprotected: true,
			},
		},
		{
			name: 'root',
			path: '/:pathMatch(.*)*',
			// Apps filtered so archived apps not shown
			redirect:
				'/apps?m=all&f=%255B%257B%2522key%2522%253A%2522archived%2522%252C%2522op%2522%253A%2522is_not%2522%257D%255D',
		},
	],
})
router.beforeEach(authGuard)

const { posthog } = usePostHog()
router.beforeEach((to, from) => {
	if (from.path !== to.path) {
		posthog.capture('$pageleave')
	}
})
router.afterEach(() => {
	posthog.capture('$pageview')
})

export default router
